import { DesignTokens, Spin } from "melco-ui";
import { useTranslation } from "react-i18next";
import { MelcoModelsUserListItem } from "melco-shared-logic";
import { SummitListFilter } from "./SummitListFilter";
import { UserResultCard } from "./item/UserResultCard";
import { useSummitUsers } from "../../hooks/summit/useSummitUsers";
import { useState, useEffect } from "react";
import { isEmpty } from "lodash-es";
import styled from "styled-components";

type SummitListPropTypes = {
  // Fired whenever the selected user or add-on changes, so the parent
  // workspace can react (e.g. auto-load that user's machines) without this
  // panel knowing anything about machines, logs, or navigation.
  onSelectionChange: (selectedUser: MelcoModelsUserListItem | null, selectedAddOn: string) => void;
};

const { spacings, colors } = DesignTokens;

const ResultsList = styled.div`
  flex: 1;
  overflow-y: auto;
  /* Bottom padding so the last card in the list isn't flush against the
     panel's edge — matches the breathing room every other edge already
     gets from the card's own padding. */
  padding-bottom: ${spacings.middle}px;
`;

const CenteredMessage = styled.div`
  padding: ${spacings.large}px ${spacings.middle}px;
  text-align: center;
  font-size: 12px;
  color: ${colors.gray.middle};
`;

export const SummitList: React.FC<SummitListPropTypes> = ({ onSelectionChange }) => {
  const { t } = useTranslation();
  const {
    isLoading,
    isFetching,
    data,
    isPreviousData,
    errorComponent,
    filtersChange,
  } = useSummitUsers();
  const [selectedUser, setSelectedUser] = useState<MelcoModelsUserListItem | null>(null);
  const [selectedAddOn, setSelectedAddOn] = useState("");
  const [searchValue, setSearchValue] = useState("");

  // Loading state combines the "first load" and "refetch while data is
  // stale" cases, same rule the previous Table-based UI used
  // (isPreviousData ? isFetching : isLoading), so behavior is unchanged.
  const showLoading = isPreviousData ? isFetching : isLoading;

  useEffect(() => {
    onSelectionChange(selectedUser, selectedAddOn);
    // onSelectionChange is expected to be a stable callback from the parent
    // (a setState function); only re-report when the selection itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, selectedAddOn]);

  // WHAT: drop the current user selection the moment the search text
  // changes (the add-on choice is left alone — see below).
  // WHY: the result list underneath the selected user is about to change
  // (or already has, once the new search resolves) — keeping an old
  // selection alive would leave a previous user's machines/logs on screen
  // next to a now-unrelated search result list. Clearing it here, rather
  // than waiting for the new list to resolve, means the parent workspace's
  // own "selectedUser changed" effect (which empties machines/logs) fires
  // immediately instead of on the next successful fetch.
  // GOTCHA: don't also clear `selectedAddOn` here. A rep commonly picks
  // the add-on first, then searches/adjusts their search for the right
  // user — wiping the add-on on every keystroke would silently discard
  // that choice before they ever got to click a user.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSelectedUser(null);
  }, [searchValue]);

  return (
    <>
      <SummitListFilter
        setSearchCallback={setSearchValue}
        onFiltersChange={filtersChange}
        selectedAddOn={selectedAddOn}
        setSelectedAddOnCallback={setSelectedAddOn}
        // A user has been clicked/selected but no add-on chosen yet — flag
        // it here so the message renders right under the Add-on field.
        showAddOnRequiredError={!!selectedUser && isEmpty(selectedAddOn)}
      />

      {errorComponent ? (
        errorComponent
      ) : showLoading ? (
        <CenteredMessage>
          <Spin />
        </CenteredMessage>
      ) : isEmpty(data) ? (
        <CenteredMessage>
          {isEmpty(searchValue)
            ? t("summit.form.panel.empty_prompt", { defaultValue: "Enter a name or email to search" })
            : t("users.table.no_data_message")}
        </CenteredMessage>
      ) : (
        <ResultsList>
          {data?.map((user: MelcoModelsUserListItem) => (
            <UserResultCard
              key={user.id}
              data-cy-id={`summit-user-result-${user.id}`}
              primaryText={
                !isEmpty(`${user.first_name ?? ""}${user.last_name ?? ""}`)
                  ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                  : user.user_name ?? ""
              }
              email={(user as any).email}
              parentUserId={(user as any).parent_user_id || undefined}
              businessName={(user as any).account_name}
              melcoCustomerId={(user as any).melco_customer_id}
              selected={selectedUser?.id === user.id}
              onClick={() => setSelectedUser(user)}
            />
          ))}
        </ResultsList>
      )}
    </>
  );
};
